import api from "./../../api";
import { getCardType, generateRandomString } from "../../helpers";
import { v4 } from 'uuid';
import isEmpty from "lodash/isEmpty";

const attractionTemplate = {
  id: v4(),
  attraction: 0,
  product_code: 0,
  prices: [],
  availability: {},
  extras: [],
};

// initial state
export const state = () => ({
  group: false,
  items: [{...attractionTemplate}],
  attractions: [],
  bookingNumber: false,
  bookingToken: false,
  options: {
    gateway: false, // card | stripe | stripe:rezdy | stripe:s3
    confirm_url: '',
  },
  customer: {
    name: "",
    phone: "",
    email: "",
    lastName: "",
    firstName: "",
  },

  creditCard: {
    cardName: "",
    cardNumber: "",
    expiryYear: "",
    expiryMonth: "",
    cardSecurityNumber: "",
    cardType: "",
    confirmationTokenId: "",
  },

  vouchers: [],
  subscribe: true,
  status: null,
  rcaNumber: "",
  booking: {},
  quote: {},
  answers: [],
  confirms: [],
  preparedPayment: {},
  payment: {
    confirmation: ''
  },
  "g-recaptcha-response": "",
});


// getters
export const getters = {
  form: (state, getters, rootState) => {

    let items = state.items;
    items = items.map((a) => {
      return {
        id: a.attraction,
        time: a.time,
        participants: a.participants,
        product_code: a.product_code ? a.product_code : '',
        extras: a.extras,
      };
    });

    let creditCard = {};

    switch (state.options.gateway.type){
      case 'card':
        creditCard = {
          ...state.creditCard,
          cardType: getCardType(state.creditCard.cardNumber)
        };
        break;
      case 'stripe:rezdy': case 'stripe:via': case 'stripe:rezdy-wallets':
        creditCard = {
          ...state.creditCard,
          cardToken: state.creditCard.cardToken
        };
        break;
      case 'stripe':
        creditCard = {
          ...state.creditCard,
          confirmationTokenId: state.payment.confirmation.id
        };

        break;
    case 'stripe:3d':
        creditCard = {
          ...state.creditCard,
          intent: state.preparedPayment?.intent?.id
        };

        break;
    }

    return {
      items: items,
      customer: state.customer,
      creditCard: creditCard,
      vouchers: state.vouchers,
      rcaNumber: state.rcaNumber,
      subscribe: state.subscribe,
      answers: state.answers,
      confirms: state.confirms,
      "g-recaptcha-response": state["g-recaptcha-response"],
    };
  },
  totalDueCents: (state, getters, rootState) => {
    return Math.round(state.quote?.totalDue * 100);
  },
  totalPeople: (state, getters, rootState) => {
    return state.items.reduce((accumulator, item) =>
      accumulator + (item.participants ? Object.entries(item.participants).reduce(
            (total, [_, count]) => total + count,
            0) : 0)
    , 0);
  },
  uniqueAttractions: (state, getters, rootState) => {
      if (!Array.isArray(state.items) || !Array.isArray(state.attractions)) {
        return [];
      }

      const attractionIds = new Set(
        state.items
          .map((item) => item?.attraction)
          .filter((id) => id !== null && id !== undefined && id !== 0 && id !== "0")
          .map((id) => String(id))
      );

      if (attractionIds.size === 0) {
        return [];
      }

      return state.attractions.filter((att) => attractionIds.has(String(att?.id)))
  }
};

// actions
export const actions = {
    quoteBooking() {
        return new Promise((resolve, reject) => {
            let savedForm = this.form;

            api
                .quote(savedForm)
                .then((response) => {
                    this.setQuote(response);
                    resolve(response);
                })
                .catch((error) => {
                    // Reject with the actual error, not in finally
                    reject(error.response || new Error('Quote booking failed'));
                });
        });
    },
    loadAttractions() {
        if (this.attractions.length > 0) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            api
                .attractions()
                .then((response) => {
                    this.setAttractions(response?.data);
                    resolve(response?.data);
                })
                .catch((error) => {
                    console.error('attractions pull error', error);
                    reject(error || new Error('Failed to load attractions'));
                });
        });
    },
    loadBooking(bookingNumber, token){
        this.bookingNumber = bookingNumber;
        this.bookingToken = token;
        return new Promise((resolve, reject) => {
            api
                .bookings.get(bookingNumber, token)
                .then((response) => {
                    this.setBooking(response.data);
                    resolve(response);
                })
                .catch((error) => {
                    console.error('load booking pull error', error);
                    reject(error.response || new Error('Failed to load booking'));
                });
        });
    },
    loadOptions() {
        if (this.options.gateway) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            api
                .options()
                .then((response) => {
                    this.setOptions(response?.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    console.error('options pull error', error);
                    reject(error || new Error('Failed to load options'));
                });
        });
    },
  setRecapture(response) {
    this["g-recaptcha-response"] = response;
  },
  setStatus(status) {
    this.status = status;
  },
  setBooking(booking) {
    this.booking = booking;
  },
  setBookingToken(bookingToken){
    this.bookingToken = bookingToken;
  },
  setQuote(quote) {
    this.quote = quote;
  },
  setItems(items) {
    this.items = items;
  },
  setItem(index, item) {
    this.items[index] = item;
  },
  addItem(item) {
    this.items.push(item);
  },
  removeItem(item) {
    this.items = this.items.filter((current) => current.id !== item.id);
  },
  clearPrices(){
    this.items = this.items.map((current) => ({...current, prices: [], availability: {}}));
  },
  async preparePayment() {
    let result;

    let args = this.form;
    args.amount = this.quote?.totalDue * 100;

    if (this.preparedPayment?.intent?.id){
      result = await api.intents.update(this.preparedPayment.intent.id, args)
    } else {
      result = await api.intents.create(args)
    }

    this.setPreparedPayment(result.data)

    return result.data;
  },
  setPreparedPayment(preparedPayment) {
    this.preparedPayment = preparedPayment;
  },
  setPayment(payment) {
    this.payment = payment;
  },

  setAttractions(attractions) {
    this.attractions = attractions;
  },
  setOptions(options) {
    this.options = options;

    if (isEmpty(this.options.gateway)){
      this.options.gateway = 'card'
    }
  },
  copyQuoteToBooking() {
    this.booking = this.quote;
    this.booking.orderNumber = generateRandomString(6);
  },
};
